import {useHistory} from '@aboutyou/router';
import {useCallback, useEffect, useRef, useState} from 'react';
import {ProductColorOptionsProps} from '../../ProductColorOptions/ProductColorOptions';

export const PRODUCT_COLOR_OPTIONS_OPEN_STATE_KEY =
  '@aboutyou/ui-components::PRODUCT_COLOR_OPTIONS_OPEN_STATE';

export const useProductColorOptionsGrid = ({
  colorOptions,
  activeColorIndex,
  onGridExpand,
  onGridCollapse,
  numberOfColorsInCollapseMode,
  uniqueKey,
}: {
  colorOptions: ProductColorOptionsProps['options'];
  activeColorIndex: number;
  onGridCollapse?: VoidFunction;
  onGridExpand?: VoidFunction;
  numberOfColorsInCollapseMode: number;
  uniqueKey: string;
}) => {
  const history = useHistory();
  const wasPreviouslyExpanded =
    (history.location.state as Record<string, boolean>)?.[
      `${PRODUCT_COLOR_OPTIONS_OPEN_STATE_KEY}${uniqueKey}`
    ] ?? false;

  const [isExpanded, setIsExpanded] = useState(wasPreviouslyExpanded);
  const [colors, setColors] = useState(colorOptions);

  useEffect(() => {
    // eslint-disable-next-line local-rules/set-state-in-use-effect
    setColors(colorOptions);
  }, [colorOptions]);

  const isActiveColorShifted = useRef(false);

  // In case the active color is not visible in the collapsed grid,
  // this function will shift the active color to the last visible element in the grid.
  const ensureActiveColorVisibility = useCallback(() => {
    if (activeColorIndex + 1 > numberOfColorsInCollapseMode - 1) {
      const updatedColors = [...colors];
      const activeColor = colors[activeColorIndex]!;
      updatedColors.splice(activeColorIndex, 1);
      updatedColors.splice(numberOfColorsInCollapseMode - 2, 0, activeColor);
      setColors(updatedColors);

      isActiveColorShifted.current = true;
    }
  }, [activeColorIndex, colors, numberOfColorsInCollapseMode]);

  const expandGrid = () => {
    // Restores the original color ordering when the grid is expanded
    if (isActiveColorShifted.current) {
      setColors([...colorOptions]);
    }
    setIsExpanded(true);
    onGridExpand?.();
  };

  const collapseGrid = () => {
    ensureActiveColorVisibility();
    setIsExpanded(false);
    onGridCollapse?.();
  };

  useEffect(() => {
    // On direct entry, this ensure that the active variant is visible if the grid is collapsed
    if (!isExpanded) {
      ensureActiveColorVisibility();
    }
  }, []);

  const shouldShowCondensedView =
    colors.length > numberOfColorsInCollapseMode && !isExpanded;

  return {
    shouldShowCondensedView,
    isExpanded,
    colors,
    expandGrid,
    collapseGrid,
  };
};
